
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import Video from 'plaid-threads/Icons/Video';
export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Learn how to integrate your app with the Link iOS SDK. Link enables the connection of your app to financial institutions.',
  preloadImages: [{
    src: '/assets/img/docs/link-ios.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link iOS SDK' subtitle='Reference for integrating with the Link iOS SDK' mdxType="Header" />
    <h4 id="overview" className="no-sticky">Overview</h4>
    <p>{`The Plaid Link SDK is a quick and secure way to link bank accounts to Plaid from within your iOS app.
LinkKit handles credential validation, multi-factor authentication, OAuth, and errors without passing sensitive information to your server.`}</p>
    <YouTubeEmbed videoId='QXHHRSSaspY' alignment='full' title='Plaid on iOS in 3 Minutes' caption='Prefer to learn by watching? Check out this quick guide to implementing Plaid on iOS devices' mdxType="YouTubeEmbed" />
    <Callout icon={<Video mdxType="Video" />} mdxType="Callout">
      <p>{`Want even more video lessons? A `}<a parentName="p" {...{
          "href": "https://youtu.be/9fgmW38usTo"
        }}>{`full tutorial`}</a>{` for integrating Plaid with iOS is available on our YouTube channel.`}</p>
    </Callout>
    <p>{`To get started, sign up for `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`free API keys`}</a>{` and try the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-ios"
      }}>{`example apps`}</a>{`.`}</p>
    <h4 id="initial-ios-setup" className="no-sticky">Initial iOS setup</h4>
    <p>{`Before writing code using the SDK, register your app with Plaid and configure Universal Links for OAuth redirects.`}</p>
    <Callout mdxType="Callout">
      <p>{`The "Register your redirect URI" and "Set up Universal Links" steps are required for OAuth. They can be skipped for Link flows that do not connect to financial institutions, such as Identity Verification or Document Income.`}</p>
    </Callout>
    <h5 {...{
      "id": "register-your-redirect-uri"
    }}>{`Register your redirect URI`}</h5>
    <ol>
      <li parentName="ol">{`Sign in to the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/signin"
        }}>{`Plaid Dashboard`}</a>{` and go to `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}><strong parentName="a">{`Build -> API`}</strong></a>{`.`}</li>
      <li parentName="ol">{`Next to `}<strong parentName="li">{`Allowed redirect URIs`}</strong>{`, click `}<strong parentName="li">{`Configure`}</strong>{`, then `}<strong parentName="li">{`Add New URI`}</strong>{`.`}</li>
      <li parentName="ol">{`Enter your redirect URI, which must also be set up as a Universal Link for your application, for example: `}<inlineCode parentName="li">{`https://app.example.com/plaid/`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`Save Changes`}</strong>{`.`}</li>
    </ol>
    <p>{`These redirect URIs must be set up as `}<a parentName="p" {...{
        "href": "https://developer.apple.com/ios/universal-links/"
      }}>{`Universal Links`}</a>{` in your application.
For details, see Apple's documentation on `}<a parentName="p" {...{
        "href": "https://developer.apple.com/documentation/xcode/allowing-apps-and-websites-to-link-to-your-content"
      }}>{`Allowing Apps and Websites to Link to Your Content`}</a>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Plaid does not support registering URLs with custom URL schemes as redirect URIs. Universal Links use a two-way association between your app and website, which helps ensure your application is opened during OAuth.`}</p>
    </blockquote>
    <h5 {...{
      "id": "set-up-universal-links"
    }}>{`Set up Universal Links`}</h5>
    <p>{`To return users to your application after a bank's OAuth flow, your redirect URI must be a Universal Link.
An example redirect URI is `}<inlineCode parentName="p">{`https://app.example.com/plaid`}</inlineCode>{`.`}</p>
    <p>{`Universal Links require:`}</p>
    <ul>
      <li parentName="ul">{`An `}<inlineCode parentName="li">{`applinks`}</inlineCode>{` entitlement for the Associated Domains capability in your app.`}</li>
      <li parentName="ul">{`An `}<inlineCode parentName="li">{`apple-app-site-association`}</inlineCode>{` file on your website.`}</li>
    </ul>
    <p>{`There are a few requirements for `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` files:`}</p>
    <ul>
      <li parentName="ul">{`Must be a static JSON file`}</li>
      <li parentName="ul">{`Must be hosted using a `}<inlineCode parentName="li">{`https://`}</inlineCode>{` scheme with a valid certificate and no redirects`}</li>
      <li parentName="ul">{`Must be hosted at `}<inlineCode parentName="li">{`https://<my-fully-qualified-domain>/.well-known/apple-app-site-association`}</inlineCode></li>
    </ul>
    <p>{`Below is an example for `}<inlineCode parentName="p">{`https://my-app.com`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #apple-app-site-association",
        "switcher": "false",
        "#apple-app-site-association": true
      }}>{`{
  "applinks": {
    "details": [
      {
        "appIDs": ["<My Application Identifier Prefix>.<My Bundle ID>"],
        "components": [
          {
            "/": "/plaid/*",
            "comment": "Matches any URL path whose path starts with /plaid/"
          }
        ]
      }
    ]
  }
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`Ensure that the corresponding entry for the configured redirect URI in the `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` file on your website continues to be available. If it is removed, OAuth sessions will fail until it is available again.`}</p>
    </blockquote>
    <h4 id="installation" className="no-sticky">Installation</h4>
    <p>{`Plaid Link for iOS is bundled with your application. For Swift integrations, we recommend using `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-ios-spm"
      }}>{`plaid-link-ios-spm`}</a>{` with Swift Package Manager.
Submitting a new version of your application with the updated `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-ios/releases"
      }}><inlineCode parentName="a">{`LinkKit.xcframework`}</inlineCode></a>{` is required to update LinkKit.`}</p>
    <h5 {...{
      "id": "requirements"
    }}>{`Requirements`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`LinkKit iOS version`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Xcode toolchain minimum support`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Supported iOS versions`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`LinkKit 7.x.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Xcode 16.1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`iOS 15 or greater`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`LinkKit 6.x.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Xcode 16.1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`iOS 14 or greater`}</td>
        </tr>
      </tbody>
    </table>
    <ContentSwitcher.Container id='LinkKit Integration' names={['Swift Package Manager', 'CocoaPods', 'Manual']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <h5 {...{
          "id": "swift-package-manager"
        }}>{`Swift Package Manager`}</h5>
        <ol>
          <li parentName="ol">{`To integrate LinkKit using Swift Package Manager, Swift version >= 5.10 is required.`}</li>
          <li parentName="ol">{`In your Xcode project from the Project Navigator (`}<strong parentName="li">{`Xcode -> View -> Navigators -> Project Cmd 1`}</strong>{`)
select your project, activate the `}<strong parentName="li">{`Package Dependencies`}</strong>{` tab and click on the plus symbol `}<strong parentName="li">{`+`}</strong>{`
to open the `}<strong parentName="li">{`Add Package`}</strong>{` popup window:`}
            <Image src='/assets/img/docs/linkkit/spm_package_dependency.png' alt="Xcode interface showing how to add a Swift package dependency; plus button highlighted." width='624' mdxType="Image" />
          </li>
          <li parentName="ol">{`Enter the LinkKit package URL `}<inlineCode parentName="li">{`https://github.com/plaid/plaid-link-ios-spm`}</inlineCode>{` into the search bar
in the top right corner of the `}<strong parentName="li">{`Add Package`}</strong>{` popup window. The main repository with full git history is very large (~1 GB), and Swift Package Manager always downloads the full repository with all git history. This `}<inlineCode parentName="li">{`plaid-link-ios-spm`}</inlineCode>{` repository is much smaller (less than 500kb), making the download faster.`}</li>
          <li parentName="ol">{`Select the `}<inlineCode parentName="li">{`plaid-link-ios-spm`}</inlineCode>{` package.`}</li>
          <li parentName="ol">{`Choose your `}<strong parentName="li">{`Dependency Rule`}</strong>{` (we recommend `}<strong parentName="li">{`Up to Next Major Version`}</strong>{`).`}</li>
          <li parentName="ol">{`Select the project to which you would like to add LinkKit, then click `}<strong parentName="li">{`Add Package`}</strong>{`:`}
            <Image src='/assets/img/docs/linkkit/spm_package_url.png' alt="Xcode screenshot showing input of Plaid Link iOS Swift package URL for package addition. 'Add Package' button highlighted." width='624' mdxType="Image" />
          </li>
          <li parentName="ol">{`Select the `}<inlineCode parentName="li">{`LinkKit`}</inlineCode>{` package product and click `}<strong parentName="li">{`Add Package`}</strong>{`:`}
            <Image src='/assets/img/docs/linkkit/spm_add_package.png' alt="Dialog in Xcode for selecting Plaid link-ios package. 'LinkKit' checked, type 'Library', target 'MySampleApp'." width='624' mdxType="Image" />
Objective-C integrations should use the same `https://github.com/plaid/plaid-link-ios-spm` package, select the `LinkKitObjC` package product instead, and import it with `@import LinkKitObjC;`.
          </li>
          <li parentName="ol">{`Verify that the LinkKit Swift package was properly added as a package dependency to your project:`}
            <Image src='/assets/img/docs/linkkit/spm_added_to_project.png' alt="Xcode showing the Plaid Link iOS package dependency from GitHub in Package Dependencies tab." width='624' mdxType="Image" />
          </li>
          <li parentName="ol">{`Select your application target and ensure that the LinkKit library is embedded into your application:`}
            <Image src='/assets/img/docs/linkkit/spm_embedded.png' alt="Xcode screenshot showing the Frameworks section with LinkKit listed under Package Dependencies for MySampleApp." width='624' mdxType="Image" />
          </li>
        </ol>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <h5 {...{
          "id": "cocoapods"
        }}>{`CocoaPods`}</h5>
        <Callout warning mdxType="Callout">
          <p>{`CocoaPods is legacy-only. LinkKit 6.x is the last series of the Plaid iOS SDK to support CocoaPods. New integrations should use Swift Package Manager.`}</p>
        </Callout>
        <p>{`If you are still on LinkKit 6.x, use:`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-bash",
            "metastring": "switcher=false #Podfile (LinkKit 6.x)",
            "switcher": "false",
            "#Podfile": true,
            "(LinkKit": true,
            "6.x)": true
          }}>{`pod 'Plaid'
`}</code></pre>
        <p>{`Then run:`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-bash",
            "metastring": "switcher=false #Install CocoaPods dependencies",
            "switcher": "false",
            "#Install": true,
            "CocoaPods": true,
            "dependencies": true
          }}>{`pod install
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <h5 {...{
          "id": "manual"
        }}>{`Manual`}</h5>
        <p>{`Get the latest `}<a parentName="p" {...{
            "href": "https://github.com/plaid/plaid-link-ios/releases"
          }}><inlineCode parentName="a">{`LinkKit.xcframework`}</inlineCode></a>{` and embed it into your application target.`}</p>
        <Image src='/assets/img/docs/linkkit/embed.jpg' alt="Xcode project with LinkKit.xcframework added under Frameworks, Libraries, and Embedded Content. LinkKit.xcframework folder in Downloads." width='624' mdxType="Image" />
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h5 {...{
      "id": "camera-support-identity-verification-only"
    }}>{`Camera Support (Identity Verification only)`}</h5>
    <Callout mdxType="Callout">
      <p>{`When using `}<a parentName="p" {...{
          "href": "/docs/identity-verification/"
        }}>{`Identity Verification`}</a>{`, add a `}<a parentName="p" {...{
          "href": "https://developer.apple.com/documentation/bundleresources/information_property_list/nscamerausagedescription"
        }}><inlineCode parentName="a">{`NSCameraUsageDescription`}</inlineCode></a>{` entry to your application's plist. iOS will crash your application if this string is not provided and Link needs camera access.`}</p>
    </Callout>
    <h5 {...{
      "id": "upgrading"
    }}>{`Upgrading`}</h5>
    <p>{`New versions of LinkKit are released frequently. We recommend updating at least once a quarter, and ideally once a month.`}</p>
    <p>{`SDK versions are supported for two years; with each major SDK release, Plaid will stop officially supporting any previous SDK versions that are more than two years old. While these older versions are expected to continue to work without disruption, Plaid will not provide assistance with unsupported SDK versions.`}</p>
    <Callout mdxType="Callout">
      <p>{`LinkKit 7 moves Swift integrations from Handler-based APIs to session-based APIs. For migration details, see the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/plaid-link-ios/blob/master/v7-migration-guide.md"
        }}>{`LinkKit 7 migration guide`}</a>{`.`}</p>
    </Callout>
    <p>{`For details on each release, see the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-ios/releases"
      }}>{`GitHub releases`}</a>{`.`}</p>
    <h4 id="opening-link" className="no-sticky">Opening Link</h4>
    <p>{`Before opening Link, create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on your server using `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`For iOS, `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` must include the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` parameter and it must match the redirect URI you configured with Plaid.`}</p>
    </blockquote>
    <h5 {...{
      "id": "standard-link"
    }}>{`Standard Link`}</h5>
    <p>{`Create a configuration, create a session, retain the session, and open it when the user is ready.`}</p>
    <Schema type='link' sdk='ios' method='create' mdxType="Schema">
      <ContentSwitcher.Container id='ios-standard-link' names={['SwiftUI', 'UIKit', 'Objective-C']} hiddenIsSearchable={true}>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-swift",
              "metastring": "switcher=false #SwiftUI",
              "switcher": "false",
              "#SwiftUI": true
            }}>{`import LinkKit
import SwiftUI

struct ConnectBankView: View {
    let linkToken: String
    @State private var linkSession: PlaidLinkSession?
    @State private var isPresentingLink = false
    @State private var isReady = false

    var body: some View {
        Button("Connect account") {
            isPresentingLink = true
        }
        .disabled(!isReady)
        .onAppear(perform: createSession)
        .sheet(isPresented: $isPresentingLink) {
            linkSession?.sheet()
        }
    }

    private func createSession() {
        let configuration = LinkTokenConfiguration(
            token: linkToken,
            onSuccess: { success in
                // Send success.publicToken to your app server.
                isPresentingLink = false
            },
            onExit: { _ in
                isPresentingLink = false
            },
            onEvent: { event in
                // Optionally handle Link events.
            },
            onLoad: {
                isReady = true
            }
        )

        do {
            linkSession = try Plaid.createPlaidLinkSession(configuration: configuration)
        } catch {
            // Handle the error.
        }
    }
}
`}</code></pre>
        </ContentSwitcher.Content>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-swift",
              "metastring": "switcher=false #UIKit",
              "switcher": "false",
              "#UIKit": true
            }}>{`import LinkKit
import UIKit

final class ConnectBankViewController: UIViewController {
    private var linkSession: PlaidLinkSession?
    private let linkToken: String = "<#LINK_TOKEN_FROM_SERVER#>"

    override func viewDidLoad() {
        super.viewDidLoad()
        createSession()
    }

    private func createSession() {
        let configuration = LinkTokenConfiguration(
            token: linkToken,
            onSuccess: { success in
                // Send success.publicToken to your app server.
            },
            onExit: { exit in
                // Optionally handle exit data.
            },
            onEvent: { event in
                // Optionally handle Link events.
            },
            onLoad: { [weak self] in
                // Enable your "Connect account" button.
            }
        )

        do {
            linkSession = try Plaid.createPlaidLinkSession(configuration: configuration)
        } catch {
            // Handle the error.
        }
    }

    @objc private func connectButtonTapped() {
        linkSession?.open(using: .viewController(self))
    }
}
`}</code></pre>
        </ContentSwitcher.Content>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-objectivec",
              "metastring": "switcher=false #Objective-C",
              "switcher": "false",
              "#Objective-C": true
            }}>{`@import LinkKitObjC;

@interface ConnectBankViewController ()
@property (nonatomic, strong) id<PLKPlaidLinkSession> linkSession;
@end

- (void)createLink {
    void (^onSuccess)(PLKLinkSuccess *) = ^(PLKLinkSuccess *success) {
        // Send success.publicToken to your app server.
    };

    PLKLinkTokenConfiguration *configuration =
      [[PLKLinkTokenConfiguration alloc] initWithToken:@"<#LINK_TOKEN_FROM_SERVER#>"
                                             onSuccess:onSuccess];

    configuration.onExit = ^(PLKLinkExit *exit) {
        // Optionally handle exit data.
    };

    configuration.onEvent = ^(PLKLinkEvent *event) {
        // Optionally handle Link events.
    };

    NSError *error = nil;
    self.linkSession = [PLKPlaid createPlaidLinkSessionWithConfiguration:configuration
                                                                   error:&error];
    if (self.linkSession == nil) {
        // Handle the error.
        return;
    }
}

- (void)connectButtonTapped {
    // Retain linkSession for the duration of the Link flow.
    [self.linkSession openWithPresentationHandler:^(UIViewController *viewController) {
        [self presentViewController:viewController animated:YES completion:nil];
    } dismissalHandler:^(UIViewController *viewController) {
        [viewController dismissViewControllerAnimated:YES completion:nil];
    }];
}
`}</code></pre>
        </ContentSwitcher.Content>
      </ContentSwitcher.Container>
    </Schema>
    <h4 {...{
      "id": "onsuccess"
    }}><inlineCode parentName="h4">{`onSuccess`}</inlineCode></h4>
    <Schema type='link' sdk='ios' method='onSuccess' mdxType="Schema">
      <ContentSwitcher.Container id='ios-onsuccess' names={['Swift', 'Objective-C']} hiddenIsSearchable={true}>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-swift",
              "metastring": "switcher=false #Swift",
              "switcher": "false",
              "#Swift": true
            }}>{`let configuration = LinkTokenConfiguration(
    token: linkToken,
    onSuccess: { success in
        // Send success.publicToken to your app server.
    },
    onExit: nil,
    onEvent: nil,
    onLoad: nil
)
`}</code></pre>
        </ContentSwitcher.Content>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-objectivec",
              "metastring": "switcher=false #Objective-C",
              "switcher": "false",
              "#Objective-C": true
            }}>{`void (^onSuccess)(PLKLinkSuccess *) = ^(PLKLinkSuccess *success) {
    // Send success.publicToken to your app server.
};

PLKLinkTokenConfiguration *configuration =
  [[PLKLinkTokenConfiguration alloc] initWithToken:@"<#LINK_TOKEN_FROM_SERVER#>"
                                         onSuccess:onSuccess];
`}</code></pre>
        </ContentSwitcher.Content>
      </ContentSwitcher.Container>
    </Schema>
    <h4 {...{
      "id": "onexit"
    }}><inlineCode parentName="h4">{`onExit`}</inlineCode></h4>
    <Schema type='link' sdk='ios' method='onExit' mdxType="Schema">
      <ContentSwitcher.Container id='ios-onexit' names={['Swift', 'Objective-C']} hiddenIsSearchable={true}>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-swift",
              "metastring": "switcher=false #Swift",
              "switcher": "false",
              "#Swift": true
            }}>{`let configuration = LinkTokenConfiguration(
    token: linkToken,
    onSuccess: { success in
        // Send success.publicToken to your app server.
    },
    onExit: { exit in
        if let error = exit.error {
            // Handle error.errorCode, error.errorMessage, and error.displayMessage.
        }

        // Use exit.metadata for support and analytics.
    },
    onEvent: nil,
    onLoad: nil
)
`}</code></pre>
        </ContentSwitcher.Content>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-objectivec",
              "metastring": "switcher=false #Objective-C",
              "switcher": "false",
              "#Objective-C": true
            }}>{`configuration.onExit = ^(PLKLinkExit *exit) {
    // Optionally handle exit.error and exit.metadata.
};
`}</code></pre>
        </ContentSwitcher.Content>
      </ContentSwitcher.Container>
    </Schema>
    <h4 {...{
      "id": "onevent"
    }}><inlineCode parentName="h4">{`onEvent`}</inlineCode></h4>
    <Schema type='link' sdk='ios' method='onEvent' mdxType="Schema">
      <ContentSwitcher.Container id='ios-onevent' names={['Swift', 'Objective-C']} hiddenIsSearchable={true}>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-swift",
              "metastring": "switcher=false #Swift",
              "switcher": "false",
              "#Swift": true
            }}>{`let configuration = LinkTokenConfiguration(
    token: linkToken,
    onSuccess: { success in
        // Send success.publicToken to your app server.
    },
    onExit: nil,
    onEvent: { event in
        // Use event.eventName and event.metadata for analytics.
    },
    onLoad: nil
)
`}</code></pre>
        </ContentSwitcher.Content>
        <ContentSwitcher.Content>
          <pre><code parentName="pre" {...{
              "className": "language-objectivec",
              "metastring": "switcher=false #Objective-C",
              "switcher": "false",
              "#Objective-C": true
            }}>{`configuration.onEvent = ^(PLKLinkEvent *event) {
    // Optionally handle event.eventName and event.eventMetadata.
};
`}</code></pre>
        </ContentSwitcher.Content>
      </ContentSwitcher.Container>
    </Schema>
    <h4 id="additional-flows" className="no-sticky">Additional flows</h4>
    <h5 {...{
      "id": "layer"
    }}>{`Layer`}</h5>
    <p>{`Layer uses a `}<inlineCode parentName="p">{`LayerTokenConfiguration`}</inlineCode>{` and a `}<inlineCode parentName="p">{`PlaidLayerSession`}</inlineCode>{`. Wait for `}<inlineCode parentName="p">{`.layerReady`}</inlineCode>{` before presenting Layer.`}</p>
    <ContentSwitcher.Container id='ios-layer' names={['SwiftUI', 'UIKit']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false #SwiftUI",
            "switcher": "false",
            "#SwiftUI": true
          }}>{`@State private var layerSession: PlaidLayerSession?
@State private var isPresentingLayer = false

var body: some View {
    EmptyView()
        .sheet(isPresented: $isPresentingLayer) {
            layerSession?.sheet()
        }
        .onAppear {
            createLayerSession()
        }
}

private func createLayerSession() {
    let configuration = LayerTokenConfiguration(
        token: linkToken,
        onSuccess: { _ in isPresentingLayer = false },
        onExit: { _ in isPresentingLayer = false },
        onEvent: { event in
            if event.eventName == .layerReady {
                isPresentingLayer = true
            }
        }
    )

    do {
        layerSession = try Plaid.createPlaidLayerSession(configuration: configuration)
    } catch {
        // Handle the error.
    }
}
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false #UIKit",
            "switcher": "false",
            "#UIKit": true
          }}>{`final class LayerViewController: UIViewController {
    private let linkToken: String = "<#LINK_TOKEN_FROM_SERVER#>"
    private var layerSession: PlaidLayerSession?

    override func viewDidLoad() {
        super.viewDidLoad()
        createLayerSession()
    }

    private func createLayerSession() {
        let configuration = LayerTokenConfiguration(
            token: linkToken,
            onSuccess: { _ in
                // Handle success.
            },
            onExit: { _ in
                // Handle exit.
            },
            onEvent: { [weak self] event in
                if event.eventName == .layerReady {
                    guard let self = self else { return }
                    self.layerSession?.open(using: .viewController(self))
                }
            }
        )

        do {
            layerSession = try Plaid.createPlaidLayerSession(configuration: configuration)
        } catch {
            // Handle the error.
        }
    }
}
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "submit"
    }}><inlineCode parentName="h4">{`submit()`}</inlineCode></h4>
    <Schema type='link' sdk='ios' method='submit' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-swift",
          "metastring": "switcher=false #Define a SubmissionData model",
          "switcher": "false",
          "#Define": true,
          "a": true,
          "SubmissionData": true,
          "model": true
        }}>{`struct PlaidSubmitData: SubmissionData {
    var phoneNumber: String?
    var dateOfBirth: String?
    var params: [String: String]?
}

let data = PlaidSubmitData(phoneNumber: "14155550015")
layerSession?.submit(data: data)
`}</code></pre>
    </Schema>
    <h5 {...{
      "id": "headless-link"
    }}>{`Headless Link`}</h5>
    <p>{`Headless Link uses a `}<inlineCode parentName="p">{`PlaidHeadlessSession`}</inlineCode>{`. Create the session, retain it, and call `}<inlineCode parentName="p">{`start()`}</inlineCode>{` when the session is ready.`}</p>
    <ContentSwitcher.Container id='ios-headless' names={['SwiftUI', 'UIKit']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false #SwiftUI",
            "switcher": "false",
            "#SwiftUI": true
          }}>{`@State private var headlessSession: PlaidHeadlessSession?

let configuration = LinkTokenConfiguration(
    token: linkToken,
    onSuccess: { success in
        // Handle success.
    },
    onExit: { exit in
        // Handle exit.
    },
    onEvent: { event in
        // Handle events.
    },
    onLoad: {
        headlessSession?.start()
    }
)

do {
    headlessSession = try Plaid.createHeadlessSession(configuration: configuration)
} catch {
    // Handle the error.
}
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false #UIKit",
            "switcher": "false",
            "#UIKit": true
          }}>{`private var headlessSession: PlaidHeadlessSession?

let configuration = LinkTokenConfiguration(
    token: linkToken,
    onSuccess: { success in
        // Handle success.
    },
    onExit: { exit in
        // Handle exit.
    },
    onEvent: { event in
        // Handle events.
    },
    onLoad: { [weak self] in
        self?.headlessSession?.start()
    }
)

do {
    headlessSession = try Plaid.createHeadlessSession(configuration: configuration)
} catch {
    // Handle the error.
}
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h5 {...{
      "id": "embedded-institution-search"
    }}>{`Embedded Institution Search`}</h5>
    <p>{`Embedded Institution Search uses `}<inlineCode parentName="p">{`EmbeddedLinkTokenConfiguration`}</inlineCode>{`. Create the embedded view before displaying it to reduce latency.`}</p>
    <ContentSwitcher.Container id='ios-embedded-search' names={['SwiftUI', 'UIKit']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false #SwiftUI",
            "switcher": "false",
            "#SwiftUI": true
          }}>{`struct EmbeddedInstitutionSearchView: View {
    let linkToken: String
    @State private var embeddedSearchView: EmbeddedSearchView?

    var body: some View {
        VStack {
            if let embeddedSearchView {
                embeddedSearchView
            }
        }
        .onAppear(perform: createEmbeddedSearchView)
    }

    private func createEmbeddedSearchView() {
        let configuration = EmbeddedLinkTokenConfiguration(
            token: linkToken,
            onSuccess: { success in
                // Handle success.
            },
            onExit: { exit in
                // Handle exit.
            },
            onEvent: { event in
                // Handle events.
            }
        )

        do {
            embeddedSearchView = try Plaid.createEmbeddedLinkView(configuration: configuration)
        } catch {
            // Handle the error.
        }
    }
}
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false #UIKit",
            "switcher": "false",
            "#UIKit": true
          }}>{`final class EmbeddedInstitutionSearchViewController: UIViewController {
    private let linkToken: String = "<#LINK_TOKEN_FROM_SERVER#>"

    override func viewDidLoad() {
        super.viewDidLoad()
        createEmbeddedSearchView()
    }

    private func createEmbeddedSearchView() {
        let configuration = EmbeddedLinkTokenConfiguration(
            token: linkToken,
            onSuccess: { success in
                // Handle success.
            },
            onExit: { exit in
                // Handle exit.
            },
            onEvent: { event in
                // Handle events.
            }
        )

        do {
            let embeddedView: EmbeddedSearchUIView = try Plaid.createEmbeddedLinkUIView(
                configuration: configuration,
                presentationMethod: .viewController(self)
            )

            embeddedView.translatesAutoresizingMaskIntoConstraints = false
            view.addSubview(embeddedView)

            NSLayoutConstraint.activate([
                embeddedView.topAnchor.constraint(equalTo: view.safeAreaLayoutGuide.topAnchor, constant: 16),
                embeddedView.leadingAnchor.constraint(equalTo: view.leadingAnchor, constant: 16),
                embeddedView.trailingAnchor.constraint(equalTo: view.trailingAnchor, constant: -16),
                embeddedView.heightAnchor.constraint(equalToConstant: embeddedView.expectedHeight),
            ])
        } catch {
            // Handle the error.
        }
    }
}
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 id="next-steps" className="no-sticky">Next steps</h4>
    <p>{`Once you've gotten Link working, see `}<a parentName="p" {...{
        "href": "/docs/link/best-practices/"
      }}>{`Link best practices`}</a>{` for recommendations on improving the Link flow.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "onsuccess",
        level: 4,
        title: "onSuccess",
      },
{
        id: "onexit",
        level: 4,
        title: "onExit",
      },
{
        id: "onevent",
        level: 4,
        title: "onEvent",
      },
{
        id: "submit",
        level: 4,
        title: "submit()",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
